import React, { useEffect, useLayoutEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import './App.css';
import Navbar from './components/Navbar';
import Breadcrumb from './components/Breadcrumb';
import Footer from './components/Footer';
import BackgroundEffects from './components/BackgroundEffects';
import HomePage from './pages/HomePage';
import CreatorsPage from './pages/CreatorsPage';
import HowToJoinPage from './pages/HowToJoinPage';
import AboutPage from './pages/AboutPage';
import EventsPage from './pages/EventsPage';
import EventManagementPage from './pages/EventManagementPage';
import ProtectedRoute from './components/ProtectedRoute';
import XboxCallbackPage from './pages/XboxCallbackPage';
import LiveEventPage from './pages/LiveEventPage';
import ControlPanelPage from './pages/ControlPanelPage';
import LostPage from './pages/LostPage';
// Store temporarily disabled while under renovation. Imports kept so re-enabling is a one-line swap.
// eslint-disable-next-line no-unused-vars
import ShopPage from './pages/ShopPage';
// eslint-disable-next-line no-unused-vars
import SuccessPage from './pages/SuccessPage';
import StoreUnavailablePage from './pages/StoreUnavailablePage';
import OverviewPage from './pages/OverviewPage';
import PartyKitHealthPage from './pages/PartyKitHealthPage';
import ThumbnailTestsPage from './pages/ThumbnailTestsPage';
import ThumbnailVotePage from './pages/ThumbnailVotePage';
import WouldYouRatherViewPage from './pages/WouldYouRatherViewPage';
import { YouTubeProvider } from './context/YouTubeContext';
import { MinecraftProvider } from './context/MinecraftContext';
import { EventsProvider } from './context/EventsContext';
import { StoreProvider } from './context/StoreContext';
import { AuthProvider } from './context/AuthContext';
import { AuthenticateWithRedirectCallback } from '@clerk/clerk-react';
import CartDrawer from './components/store/CartDrawer';
import { NotificationProvider } from './context/NotificationContext';
import NotificationContainer from './components/Notification';
import EventTransition from './components/EventTransition';
import { PanelProvider } from './context/PanelContext';
import { Analytics } from '@vercel/analytics/react';
import './font-override.css';

const pageTitles = {
  '/': 'Home',
  '/creators': 'Creators',
  '/how-to-join': 'How to Join',
  '/about': 'About',
  '/events': 'Events',
  '/shop': 'Store Unavailable',
  '/shop/success': 'Store Unavailable',
  '/panel': 'Overview',
  '/panel/event-management': 'Event Management',
  '/panel/partykit': 'PartyKit Health',
  '/panel/thumbnails': 'Thumbnails',
  '/xbox-callback': 'Linking Minecraft',
};

// Separate component for instant title updates
function TitleUpdater() {
  const location = useLocation();

  useLayoutEffect(() => {
    let pageTitle = pageTitles[location.pathname];
    if (!pageTitle) {
      if (location.pathname.startsWith('/event/')) pageTitle = 'Live Event';
      else if (location.pathname.startsWith('/panel/event-management/control/')) pageTitle = 'Control Panel';
      else if (location.pathname.startsWith('/vote/')) {
        document.title = 'Thumbnail Test';
        return;
      }
      else pageTitle = 'Page Not Found';
    }
    document.title = `${pageTitle} - TheTopia (Minecraft Events)`;
  }, [location.pathname]);

  return null;
}

function PageWrapper({ children }) {
  const location = useLocation();

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('in-view');
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    // Helper to check if element is in viewport
    const isInViewport = (el) => {
      const rect = el.getBoundingClientRect();
      return (
        rect.top < window.innerHeight &&
        rect.bottom > 0 &&
        rect.left < window.innerWidth &&
        rect.right > 0
      );
    };

    // Small delay to ensure DOM is ready after route change
    const timer = setTimeout(() => {
      const animatedElements = document.querySelectorAll(
        '.animate-fade-in:not(.in-view), .animate-slide-up:not(.in-view), .animate-slide-left:not(.in-view), .animate-slide-right:not(.in-view), .animate-scale-in:not(.in-view), .animate-pop-in:not(.in-view)'
      );

      animatedElements.forEach(el => {
        // If element is already in viewport, animate immediately
        if (isInViewport(el)) {
          el.classList.add('in-view');
        } else {
          observer.observe(el);
        }
      });
    }, 50);

    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [location.pathname]);

  return (
    <div className="page-transition" key={location.pathname}>
      {children}
    </div>
  );
}

function PanelRoutes() {
  return (
    <Routes>
      <Route path="/" element={<PageWrapper><OverviewPage /></PageWrapper>} />
      <Route path="/event-management" element={<PageWrapper><EventManagementPage /></PageWrapper>} />
      <Route path="/partykit" element={<PageWrapper><PartyKitHealthPage /></PageWrapper>} />
      <Route path="/thumbnails" element={<PageWrapper><ThumbnailTestsPage /></PageWrapper>} />
      <Route path="/event-management/control/:slug" element={<PageWrapper><ControlPanelPage /></PageWrapper>} />
    </Routes>
  );
}

export default function App() {
  const location = useLocation();
  const isPanelPage = location.pathname === '/panel' || location.pathname.startsWith('/panel/');
  const isVotePage = location.pathname.startsWith('/vote/');

  return (
    <AuthProvider>
    <NotificationProvider>
    <MinecraftProvider>
    <YouTubeProvider>
    <EventsProvider>
    <StoreProvider>
      <div className={`app${isPanelPage ? ' app-panel' : ''}${isVotePage ? ' app-vote' : ''}`}>
        <TitleUpdater />
        <BackgroundEffects />
        <Navbar />
        <CartDrawer />
        {/* Hidden for now
        <Breadcrumb />
        */}
        <Routes>
          <Route path="/" element={<PageWrapper><HomePage /></PageWrapper>} />
          <Route path="/creators" element={<PageWrapper><CreatorsPage /></PageWrapper>} />
          <Route path="/how-to-join" element={<PageWrapper><HowToJoinPage /></PageWrapper>} />
          <Route path="/about" element={<PageWrapper><AboutPage /></PageWrapper>} />
          <Route path="/events" element={<PageWrapper><EventsPage /></PageWrapper>} />
          {/* Store temporarily disabled — restore the two lines below to re-enable Tebex shop & success page.
          <Route path="/shop" element={<PageWrapper><ShopPage /></PageWrapper>} />
          <Route path="/shop/success" element={<PageWrapper><SuccessPage /></PageWrapper>} />
          */}
          <Route path="/shop" element={<PageWrapper><StoreUnavailablePage /></PageWrapper>} />
          <Route path="/shop/success" element={<PageWrapper><StoreUnavailablePage /></PageWrapper>} />
          <Route path="/sso-callback" element={<AuthenticateWithRedirectCallback signInFallbackRedirectUrl="/panel" signUpFallbackRedirectUrl="/panel" />} />
          <Route path="/xbox-callback" element={<PageWrapper><ProtectedRoute minimumRole="viewer"><XboxCallbackPage /></ProtectedRoute></PageWrapper>} />
          <Route path="/event/:slug" element={<PageWrapper><LiveEventPage /></PageWrapper>} />
          <Route path="/event/:slug/view" element={<PageWrapper><WouldYouRatherViewPage /></PageWrapper>} />
          <Route path="/vote/:slug" element={<PageWrapper><ThumbnailVotePage /></PageWrapper>} />
          <Route path="/panel/*" element={<ProtectedRoute minimumRole="admin" showShell><PanelProvider><PanelRoutes /></PanelProvider></ProtectedRoute>} />
          <Route path="*" element={<PageWrapper><LostPage /></PageWrapper>} />
        </Routes>
        <Footer />
        <NotificationContainer />
        <EventTransition />
        <Analytics />
      </div>
    </StoreProvider>
    </EventsProvider>
    </YouTubeProvider>
    </MinecraftProvider>
    </NotificationProvider>
    </AuthProvider>
  );
}
